import React from 'react';
import { BlurUpImage } from '../../../shared/components/BlurUpImage';
import {
  MARKETING_PLACEHOLDERS,
  MARKETING_DOMINANT_COLORS,
} from '../../../shared/marketing-placeholders';

/**
 * Capacity Clarity card: a compact framed snapshot of the Relix capacity calendar
 * with a fictional-names legend overlay. Image is shown small so it never
 * dominates the page; the legend is the readable proof.
 */

type LegendName = { id: string; name: string; tone: 'violet' | 'amber' | 'coral' | 'teal' };

const LEGEND: readonly LegendName[] = [
  { id: 'anna', name: 'Anna Petrova', tone: 'amber' },
  { id: 'mark', name: 'Mark Evans', tone: 'coral' },
  { id: 'elena', name: 'Elena Novak', tone: 'violet' },
  { id: 'julia', name: 'Julia Smirnova', tone: 'teal' },
] as const;

const CAPACITY_SUMMARY: readonly { label: string; value: string; tone?: 'warn' }[] = [
  { label: 'Team members affected', value: '3' },
  { label: 'Absences', value: '7 days' },
  { label: 'Calendar events', value: '11' },
  { label: 'Availability impact', value: '−18.5 days', tone: 'warn' },
] as const;

export function CapacityCalendarFrame(): React.ReactElement {
  return (
    <div className="capacity-clarity">
      <div className="capacity-clarity__frame product-window">
        <div className="capacity-clarity__viewport">
          <BlurUpImage
            src="/brand/solutions/capacity-moment.png"
            webpSrc="/brand/solutions/capacity-moment-original.webp"
            placeholderSrc={MARKETING_PLACEHOLDERS['capacity-moment']}
            dominantColor={MARKETING_DOMINANT_COLORS['capacity-moment']}
            alt="Relix capacity calendar with absences, holidays, and team events"
            width={1280}
            height={720}
            className="capacity-clarity__shot"
          />
          <span className="capacity-clarity__shot-veil" aria-hidden="true" />
        </div>
      </div>

      <aside className="capacity-clarity__legend" aria-label="Availability impact">
        <header className="capacity-clarity__legend-head">
          <span className="capacity-clarity__legend-eyebrow">Availability impact</span>
          <h3 className="capacity-clarity__legend-title">Real availability, not assumptions.</h3>
        </header>
        <ul className="capacity-clarity__summary">
          {CAPACITY_SUMMARY.map((row) => (
            <li
              key={row.label}
              className={`capacity-clarity__summary-row${
                row.tone === 'warn' ? ' capacity-clarity__summary-row--warn' : ''
              }`}
            >
              <span className="capacity-clarity__summary-label">{row.label}</span>
              <span className="capacity-clarity__summary-value">{row.value}</span>
            </li>
          ))}
        </ul>
        <ul className="capacity-clarity__people" aria-label="Team in view this sprint">
          {LEGEND.map((p) => (
            <li key={p.id} className="capacity-clarity__person">
              <span
                className={`capacity-clarity__person-dot capacity-clarity__person-dot--${p.tone}`}
                aria-hidden="true"
              />
              <span className="capacity-clarity__person-name">{p.name}</span>
            </li>
          ))}
        </ul>
      </aside>
    </div>
  );
}
